import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { AlertTriangle, ScanLine, Undo2, X } from "lucide-react";
import { BackButton } from "@/components/BackButton";
import { useApp } from "@/context/AppContext";
import {
  fetchIntakeSession,
  intakeScanRequest,
  intakeUndoRequest,
  setIntakeSession,
  type IntakeBin,
} from "@/context/inventorySync";
import { containerPath, formatContainerPath } from "@/features/containerPath";

export const Route = createFileRoute("/tools/intake")({
  component: ChaosIntakePage,
});

const CONDITIONS = ["NM", "LP", "MP", "HP", "Damaged"];
const GAMES = ["Pokemon", "Magic"] as const;
type Game = (typeof GAMES)[number];

/** A row in the running session list. `ok=false` is a not-found scan. */
interface ScanEntry {
  key: number;
  ok: boolean;
  name: string;
  setName?: string;
  number?: string;
  image?: string;
  condition?: string;
  created?: boolean;
  raw: string;
}

/** Parse a typed/scanned entry into set code + collector number. The hot path
 *  is whitespace-separated ("swsh3 170"); we also accept a single "set-number"
 *  token by splitting on the first hyphen when there's no space. */
function parseEntry(raw: string): { setCode: string; number: string } | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  let parts = trimmed.split(/\s+/);
  if (parts.length === 1 && trimmed.includes("-")) {
    const i = trimmed.indexOf("-");
    parts = [trimmed.slice(0, i), trimmed.slice(i + 1)];
  }
  if (parts.length < 2) return null;
  const setCode = parts[0];
  const number = parts.slice(1).join(" ");
  if (!setCode || !number) return null;
  return { setCode, number };
}

/**
 * Chaos intake (TNK-188). Pick an active bin once, then scan/type
 * `set_code + collector_number`; each scan resolves the card and drops a +1
 * into the bin. Mobile-first — operators do this standing at the shelves.
 * Undo-last is mandatory; the capacity warning is soft (never blocks).
 */
function ChaosIntakePage() {
  const { containers, mergeServerCards } = useApp();

  const [game, setGame] = useState<Game>("Pokemon");
  const [condition, setCondition] = useState("NM");
  const [activeBinId, setActiveBinId] = useState<string | null>(null);
  const [bin, setBin] = useState<IntakeBin | null>(null);
  const [entries, setEntries] = useState<ScanEntry[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const keyRef = useRef(0);

  // TNK-196: any leaf container is a valid intake target (bin/box/tab/binder),
  // not just bins. Show the full path so a tab inside a box is unambiguous.
  const containerById = useMemo(
    () => new Map(containers.map((c) => [c.id, c])),
    [containers],
  );
  const binOptions = useMemo(
    () =>
      containers
        .filter(
          (c) =>
            ["bin", "box", "tab", "binder"].includes(c.kind) && !c.archivedAt,
        )
        .map((c) => ({
          id: c.id,
          label: formatContainerPath(containerPath(c.id, containerById)),
          capacityMax: c.capacityMax,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [containers, containerById],
  );

  // Resume the operator's last active bin on mount.
  useEffect(() => {
    let cancelled = false;
    fetchIntakeSession()
      .then((s) => {
        if (cancelled) return;
        setActiveBinId(s.containerId);
        setBin(s.bin);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Keep the scan input focused so a wedge scanner / fast typing always lands.
  useEffect(() => {
    if (activeBinId && !busy) inputRef.current?.focus();
  }, [activeBinId, busy, entries.length]);

  const scannedCount = entries.filter((e) => e.ok).length;
  const overCapacity = bin?.capacityMax != null && bin.total >= bin.capacityMax;
  const nearCapacity =
    !overCapacity &&
    bin?.capacityMax != null &&
    bin.total >= Math.floor(bin.capacityMax * 0.9);

  async function chooseBin(id: string) {
    if (!id) return;
    try {
      const s = await setIntakeSession(id);
      setActiveBinId(s.containerId);
      setBin(s.bin);
      setToast(null);
      inputRef.current?.focus();
    } catch {
      setToast("Could not set the active bin. Refresh and retry.");
    }
  }

  async function submitScan(e: React.FormEvent) {
    e.preventDefault();
    const parsed = parseEntry(input);
    if (!parsed) {
      setToast("Type set code then number, e.g. “swsh3 170”.");
      return;
    }
    if (!activeBinId) {
      setToast("Pick an active bin first.");
      return;
    }
    setBusy(true);
    setToast(null);
    const raw = input.trim();
    try {
      const res = await intakeScanRequest({
        game,
        setCode: parsed.setCode,
        number: parsed.number,
        condition,
      });
      if (!res.matched || !res.card) {
        setEntries((prev) => [
          { key: keyRef.current++, ok: false, name: "Not found", raw },
          ...prev,
        ]);
        setToast(`No ${game} match for “${raw}”. Check the set code + number.`);
      } else {
        mergeServerCards([res.card]);
        setEntries((prev) => [
          {
            key: keyRef.current++,
            ok: true,
            name: res.card!.name,
            setName: res.card!.setName,
            number: res.card!.cardNumber,
            image: res.card!.imageFrontSourceUrl,
            condition: res.card!.condition,
            created: res.created,
            raw,
          },
          ...prev,
        ]);
        if (typeof res.binTotal === "number") {
          setBin((b) => (b ? { ...b, total: res.binTotal! } : b));
        }
      }
      setInput("");
    } catch {
      setToast("Scan failed — the server didn’t record it. Try again.");
    } finally {
      setBusy(false);
      inputRef.current?.focus();
    }
  }

  async function undoLast() {
    setBusy(true);
    setToast(null);
    try {
      const res = await intakeUndoRequest();
      if (!res.undone) {
        setToast("Nothing to undo.");
        return;
      }
      if (res.card) mergeServerCards([res.card]);
      // Drop the most recent successful row from the visible list.
      setEntries((prev) => {
        const idx = prev.findIndex((e) => e.ok);
        if (idx === -1) return prev;
        return prev.filter((_, i) => i !== idx);
      });
      if (typeof res.binTotal === "number") {
        setBin((b) => (b ? { ...b, total: res.binTotal! } : b));
      }
    } catch {
      setToast("Undo failed. Refresh to re-sync.");
    } finally {
      setBusy(false);
      inputRef.current?.focus();
    }
  }

  return (
    <section className="flex h-full flex-col">
      <div className="flex items-center justify-between gap-2 pb-3">
        <BackButton fallback={{ to: "/tools" }} label="Tools" />
        <div className="text-xs text-muted-foreground">
          {scannedCount} scanned this session
        </div>
      </div>

      <div className="border-b border-border pb-3">
        <h1 className="text-2xl font-bold text-foreground">Chaos Intake</h1>
        <p className="text-sm text-muted-foreground">
          Pick a bin, then scan or type{" "}
          <span className="font-mono">set&nbsp;number</span> to drop each card
          in. The database remembers where it went — no sorting required.
        </p>
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto py-4">
        {/* Controls: bin, condition, game */}
        <div className="grid gap-3 sm:grid-cols-3">
          <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
            Active container
            <select
              value={activeBinId ?? ""}
              onChange={(e) => void chooseBin(e.target.value)}
              className="rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground"
            >
              <option value="" disabled>
                {binOptions.length
                  ? "Choose a container…"
                  : "No containers — create one"}
              </option>
              {binOptions.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                  {c.capacityMax != null ? ` (cap ${c.capacityMax})` : ""}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
            Default condition
            <select
              value={condition}
              onChange={(e) => setCondition(e.target.value)}
              className="rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground"
            >
              {CONDITIONS.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
            Game
            <select
              value={game}
              onChange={(e) => setGame(e.target.value as Game)}
              className="rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground"
            >
              {GAMES.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
          </label>
        </div>

        {/* Capacity soft-warning — never blocks */}
        {(nearCapacity || overCapacity) && bin && (
          <div
            className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm ${
              overCapacity
                ? "border-amber-500/40 bg-amber-500/10 text-amber-700"
                : "border-amber-500/20 bg-amber-500/5 text-amber-700/90"
            }`}
          >
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>
              {overCapacity
                ? `Bin “${bin.name}” is full (${bin.total}/${bin.capacityMax}). Open the next bin?`
                : `Bin “${bin.name}” is nearly full (${bin.total}/${bin.capacityMax}).`}
            </span>
          </div>
        )}

        {/* Scan input + undo */}
        <form onSubmit={submitScan} className="flex items-center gap-2">
          <div className="relative flex-1">
            <ScanLine className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={!activeBinId || busy}
              autoFocus
              autoComplete="off"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              placeholder={
                activeBinId ? "Scan or type: set number" : "Pick a bin first"
              }
              className="w-full rounded-md border border-border bg-card py-2.5 pl-9 pr-3 text-base text-foreground outline-none focus:border-primary disabled:opacity-50"
            />
          </div>
          <button
            type="submit"
            disabled={!activeBinId || busy}
            className="rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            Add
          </button>
          <button
            type="button"
            onClick={() => void undoLast()}
            disabled={busy || scannedCount === 0}
            className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-3 py-2.5 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50"
            title="Undo last scan"
          >
            <Undo2 className="h-4 w-4" />
            Undo
          </button>
        </form>

        {toast && (
          <div className="flex items-center justify-between gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            <span>{toast}</span>
            <button
              type="button"
              onClick={() => setToast(null)}
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Running session list */}
        <div className="space-y-1">
          {entries.length === 0 ? (
            <div className="rounded-md border border-dashed border-border py-8 text-center text-sm text-muted-foreground">
              Scanned cards appear here. The newest is on top.
            </div>
          ) : (
            entries.map((e) => (
              <div
                key={e.key}
                className={`flex items-center gap-3 rounded-md border px-3 py-2 ${
                  e.ok
                    ? "border-border bg-card"
                    : "border-destructive/30 bg-destructive/5"
                }`}
              >
                {e.ok && e.image ? (
                  <img
                    src={e.image}
                    alt=""
                    className="h-10 w-7 shrink-0 rounded object-cover"
                  />
                ) : (
                  <div className="flex h-10 w-7 shrink-0 items-center justify-center rounded bg-muted text-muted-foreground">
                    <ScanLine className="h-4 w-4" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-foreground">
                    {e.name}
                    {e.ok && e.created && (
                      <span className="ml-2 rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">
                        new
                      </span>
                    )}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">
                    {e.ok
                      ? `${e.setName ?? ""} ${e.number ? `#${e.number}` : ""} · ${e.condition}`
                      : `“${e.raw}” — not found`}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </section>
  );
}
